import React from 'react';
import PropTypes from 'prop-types';
import injectSheet from 'react-jss';
import { connect } from 'react-redux';
import { compose } from 'redux';
import cn from 'classnames';
import theme, {
  container,
  getFontFamilyAndWeightFor,
  sliceColourTheme,
  sliceFontStyle,
  expandAbsolutely,
  getBackgroundImageWidth,
  getBackgroundColour,
} from '../styles/theme';
import {
  getSliceCopy,
  getSliceContent,
  getSliceBottomMarginSize,
  getSliceAnchorId,
  getSliceColourTheme,
  getSliceBackgroundImage,
  getSliceBackgroundImageWidth,
  getSliceFontStyle,
} from '../selectors';
import get from '../helpers/get';
import FullViewportContainer from './FullViewportContainer';
import ResponsiveImage from './ResponsiveImage';
import { colourThemeType, backgroundImageWidthType } from '../propTypes';

const PullQuote = ({
  classes,
  copy,
  bottomMarginSize,
  subtitle,
  attribution,
  anchorId,
  backgroundImage,
}) => (
  <FullViewportContainer id={anchorId} className={classes.container}>
    <aside className={cn(classes.copy, { tight: bottomMarginSize === 'Regular' })}>
      {subtitle && (
        <span className={classes.subtitle}>
          {subtitle}
        </span>
      )}
      {copy}
      {attribution && (
        <span className={classes.attribution}>
          {attribution}
        </span>
      )}
    </aside>
    {backgroundImage && (
      <ResponsiveImage
        aspect={backgroundImage.aspect}
        className={classes.backgroundImage}
        images={backgroundImage.sizes}
        blur={backgroundImage.blur}
      />
    )}
  </FullViewportContainer>
);

PullQuote.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  copy: PropTypes.string,
  // eslint-disable-next-line react/no-unused-prop-types -- used in mapStateToProps
  fontStyle: PropTypes.string,
  bottomMarginSize: PropTypes.string,
  subtitle: PropTypes.string,
  attribution: PropTypes.string,
  anchorId: PropTypes.string,
  backgroundImage: PropTypes.shape({
    blur: PropTypes.object,
    sizes: PropTypes.array,
    aspect: PropTypes.number,
  }),
  // eslint-disable-next-line react/no-unused-prop-types -- used in mapStateToProps
  colourTheme: colourThemeType,
  // eslint-disable-next-line react/no-unused-prop-types -- used in the styles
  backgroundImageWidth: backgroundImageWidthType,
};

export const defaultFontStyle = sliceFontStyle['Heading (Didot)'];
export const defaultColourTheme = sliceColourTheme.white;

PullQuote.defaultProps = {
  copy: undefined,
  fontStyle: defaultFontStyle,
  bottomMarginSize: undefined,
  subtitle: undefined,
  attribution: undefined,
  anchorId: undefined,
  backgroundImage: undefined,
  colourTheme: defaultColourTheme,
  backgroundImageWidth: 'Edge-to-edge',
};

const mapStateToProps = (state, { sliceId }) => ({
  copy: getSliceCopy(state, sliceId),
  subtitle: get(getSliceContent(state, sliceId), ['subtitle']),
  attribution: get(getSliceContent(state, sliceId), ['attribution']),
  fontStyle: sliceFontStyle[getSliceFontStyle(state, sliceId)],
  bottomMarginSize: getSliceBottomMarginSize(state, sliceId),
  anchorId: getSliceAnchorId(state, sliceId),
  backgroundImage: getSliceBackgroundImage(state, sliceId),
  colourTheme: sliceColourTheme[getSliceColourTheme(state, sliceId)],
  backgroundImageWidth: getSliceBackgroundImageWidth(state, sliceId),
});

export default compose(
  connect(mapStateToProps),
  injectSheet({
    container: {
      position: 'relative',
      backgroundColor: ({
        colourTheme: { backgroundColour }, backgroundImage,
      }) => getBackgroundColour(backgroundColour, backgroundImage),
      display: 'flex',
    },
    copy: {
      zIndex: 1,
      maxWidth: 768 + theme.padding.horizontal * 2,
      padding: container.padding,
      color: ({ colourTheme: { textColour } }) => textColour,
      fontFamily: ({ fontStyle }) => fontStyle,
      fontSize: 28,
      [theme.breakpoints.up('lg')]: {
        fontSize: 40,
      },
      letterSpacing: '0.3px',
      lineHeight: 1.5,
      textAlign: 'center',
      // we take advantage of the collapsing margin above
      margin: ['80px', 'auto'],
      // This is to bring it close to the text below, if there is any
      '&.tight': {
        margin: ['80px', 'auto', '56px'],
      },
    },
    subtitle: {
      ...getFontFamilyAndWeightFor('bold'),
      textTransform: 'uppercase',
      display: 'block',
      letterSpacing: 1,
      fontSize: 16,
      marginBottom: theme.spacing.vertical.lg,
      lineHeight: 1.5,
    },
    attribution: {
      extend: 'subtitle',
      marginBottom: 0,
      marginTop: theme.spacing.vertical.lg,
    },
    backgroundImage: {
      ...expandAbsolutely,
      maxWidth: ({ backgroundImageWidth }) => getBackgroundImageWidth(backgroundImageWidth),
    },
  }),
)(PullQuote);
